import {
  cloneElement,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

import { useOuterClick } from "../../utils";
import { MenuOptions, OptionProps, overflowStyle } from "./Menu.options";

export type Position = "left" | "right" | "center";

export enum MenuTestIds {
  Component = "menu",
}

const Container = styled.div`
  position: relative;
  width: max-content;
`;

type MenuProps<T extends HTMLElement> = {
  id?: string;
  className?: string;
  testId?: string;
  trigger: JSX.Element;
  options?: OptionProps[];
  opened?: boolean;
  /** top specifies whether the Menu will appear above the Trigger. It appears below by default */
  top?: boolean;
  position?: Position;
  disabled?: boolean;
  autoClose?: boolean;
  autoCloseOptions?: {
    excludeRefs?: RefObject<T>[];
    excludeIds?: string[];
  };
  /** Height in px, Not compatible with multi-level menus */
  maxHeight?: number;
};

export const Menu = <T extends HTMLElement>({
  id,
  className,
  testId,
  trigger,
  options = [],
  opened = false,
  top = false,
  position = "right",
  disabled,
  autoClose,
  autoCloseOptions,
  maxHeight,
}: MenuProps<T>) => {
  const [isOpen, setIsOpen] = useState(opened);
  const containerRef = useRef(null);
  const handleOuterClick = () => setIsOpen(false);
  const outerClickRefs = useMemo(
    () => [containerRef, ...(autoCloseOptions?.excludeRefs || [])],
    [autoCloseOptions?.excludeRefs],
  );
  useOuterClick(outerClickRefs, handleOuterClick, {
    ids: autoCloseOptions?.excludeIds,
    disabled: !autoClose,
  });

  useEffect(() => {
    setIsOpen(opened);
  }, [opened]);

  return (
    <Container
      id={id}
      className={className}
      data-testid={testId ?? MenuTestIds.Component}
      ref={containerRef}
    >
      {cloneElement(trigger, {
        disabled,
        ...(autoClose && {
          onClick: () => setIsOpen((prevState) => !prevState),
        }),
      })}
      {isOpen && !disabled && (
        <MenuOptions
          top={top}
          options={options}
          position={position}
          style={overflowStyle(maxHeight)}
        />
      )}
    </Container>
  );
};
